export interface LessonSummary {
  title: string;
  sections: Array<{
    heading: string;
    bullets: string[];
  }>;
}

export interface Flashcard {
  id: string;
  front: string;
  back: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
  type: 'multiple-choice';
}

export interface SimilarLessonItem {
  id: string;
  title: string;
  subject: string;
  description: string;
  generated: boolean;
}

export interface TeachStyle {
  style: string;
  title: string;
  content: string[];
  icon: string;
}

export interface NarratorSegment {
  time: number;
  text: string;
}

export interface PracticeTest {
  id: string;
  title: string;
  duration: number;
  questionCount: number;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface Worksheet {
  id: string;
  title: string;
  problemCount: number;
  completed: boolean;
}

export interface HomeworkHelp {
  id: string;
  question: string;
  aiAnswer: string;
  userRating: number;
}

export interface RelatedVideo {
  id: string;
  title: string;
  subject: string;
  duration: number;
  thumbnail: string;
  lessonId: string;
}

export interface SavedLesson {
  id: string;
  subject: string;
  title: string;
  subtitle: string;
  duration: number;
  scannedImage: string;
  createdAt: string;
  summary: LessonSummary;
  flashcards: Flashcard[];
  quiz: QuizQuestion[];
  teachDifferently: TeachStyle[];
  similarLessons: SimilarLessonItem[];
  keyConcepts: string[];
  narratorScript: NarratorSegment[];
  previewImage: string;
  description: string;
  videoThumbnail: string;
  aiSummary: string;
  practiceTests: PracticeTest[];
  worksheets: Worksheet[];
  homeworkHelp: HomeworkHelp[];
  relatedVideos: RelatedVideo[];
  completionPercent: number;
  topicDifficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedStudyTime: number;
  lastWatched: string;
  aiTutorNotes: string;
}

export const userPlan = {
  tier: 'free' as 'free' | 'premium' | 'pro',
  similarLessonsGenerated: 0,
  similarLessonsMax: 1,
  downloadsEnabled: false,
  advancedQuiz: false,
  premiumVoice: false,
};

const relatedVideoThumbs: Record<string, string> = {
  rv1: 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-derivatives-slope-tangent-line-graph-clean-geometric-on-white-background-premium-academic-flat-2d-vector-aesthetic&width=180&height=120&seq=rv1&orientation=landscape',
  rv2: 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-physics-momentum-collision-vector-arrows-clean-geometric-on-white-background-premium-academic-flat-2d-vector-aesthetic&width=180&height=120&seq=rv2&orientation=landscape',
  rv3: 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-chemistry-periodic-table-elements-atomic-structure-clean-geometric-on-white-background-premium-academic-flat-2d-vector-aesthetic&width=180&height=120&seq=rv3&orientation=landscape',
  rv4: 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-calculus-limits-continuity-function-curve-clean-geometric-on-white-background-premium-academic-flat-2d-vector-aesthetic&width=180&height=120&seq=rv4&orientation=landscape',
  rv5: 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-physics-energy-conservation-workforce-diagram-clean-geometric-on-white-background-premium-academic-flat-2d-vector-aesthetic&width=180&height=120&seq=rv5&orientation=landscape',
  rv6: 'https://readdy.ai/api/search-image?query=minimal-monochrome-black-and-white-educational-illustration-chemistry-stereochemistry-chirality-hand-mirror-image-clean-geometric-on-white-background-premium-academic-flat-2d-vector-aesthetic&width=180&height=120&seq=rv6&orientation=landscape',
};

// Production-ready: no demo lessons. Lessons populate from real user scans.
export const savedLessons: SavedLesson[] = [];

export function getLessonById(id: string): SavedLesson | undefined {
  return savedLessons.find((l) => l.id === id);
}